import React from 'react';
import { useCurrentAccount, useWallets, useConnectWallet, useDisconnectWallet } from '@mysten/dapp-kit';

const EnokiLoginButton = ({
    className = '',
    disabled = false
}) => {
    const currentAccount = useCurrentAccount();
    const wallets = useWallets();
    const { mutate: connect } = useConnectWallet();
    const { mutate: disconnect } = useDisconnectWallet();

    // 找到 Google zkLogin 錢包
    const googleWallet = wallets.find(wallet =>
        wallet.name.toLowerCase().includes('google') ||
        wallet.name.toLowerCase().includes('zklogin')
    );

    const handleLogin = () => {
        if (disabled) return;

        if (currentAccount) {
            // 已登入，執行登出
            disconnect();
        } else {
            // 未登入，執行登入
            if (googleWallet) {
                connect(
                    { wallet: googleWallet },
                    {
                        onSuccess: () => {
                            console.log('✅ Google zkLogin connected successfully');
                        },
                        onError: (error) => {
                            console.error('❌ Google zkLogin connection failed:', error);
                        }
                    }
                );
            } else {
                console.error('❌ Google zkLogin wallet not found');
                alert('Google zkLogin wallet not available. Please check Enoki configuration.');
            }
        }
    };

    return (
        <>
            <button
                className={`enoki-login-btn ${currentAccount ? 'logged-in' : ''} ${className}`}
                onClick={handleLogin}
                disabled={disabled}
            >
                {currentAccount ? (
                    <>
                        <svg className="btn-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                            <polyline points="16 17 21 12 16 7" />
                            <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                        <span className="btn-text">Sign Out</span>
                    </>
                ) : (
                    <>
                        <svg className="btn-icon google-icon" width="20" height="20" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        <span className="btn-text">Continue with Google</span>
                    </>
                )}
            </button>

            <style>{`
                .enoki-login-btn {
                    /* Layout */
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 12px;
                    width: 100%;
                    padding: 14px 24px;
                    
                    /* Typography */
                    font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', 'Roboto', sans-serif;
                    font-size: 15px;
                    font-weight: 500;
                    letter-spacing: -0.01em;
                    
                    /* Colors */
                    background: #ffffff;
                    color: #1d1d1f;
                    border: 1px solid rgba(0, 0, 0, 0.08);
                    
                    /* Effects */
                    border-radius: 12px;
                    box-shadow: 
                        0 1px 2px rgba(0, 0, 0, 0.04),
                        0 2px 8px rgba(0, 0, 0, 0.04);
                    
                    /* Interaction */
                    cursor: pointer;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative;
                    overflow: hidden;
                }

                .enoki-login-btn::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%);
                    opacity: 0;
                    transition: opacity 0.2s ease;
                }

                .enoki-login-btn:hover {
                    transform: translateY(-1px);
                    color: #1d1d1f;
                    box-shadow: 
                        0 2px 4px rgba(0, 0, 0, 0.06),
                        0 4px 16px rgba(0, 0, 0, 0.08);
                    border-color: rgba(0, 0, 0, 0.12);
                }

                .enoki-login-btn:hover::before {
                    opacity: 1;
                }

                .enoki-login-btn:active {
                    transform: translateY(0);
                    color: #1d1d1f;
                    box-shadow: 
                        0 1px 2px rgba(0, 0, 0, 0.04),
                        0 2px 6px rgba(0, 0, 0, 0.06);
                }

                .enoki-login-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                    transform: none;
                }

                .enoki-login-btn.logged-in {
                    background: #f5f5f7;
                    color: #1d1d1f;
                    border-color: rgba(0, 0, 0, 0.06);
                }

                .enoki-login-btn.logged-in:hover {
                    background: #ebebed;
                }

                .btn-icon {
                    flex-shrink: 0;
                    transition: transform 0.2s ease;
                }

                .enoki-login-btn:hover .btn-icon {
                    transform: scale(1.05);
                }

                .google-icon {
                    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
                }

                .btn-text {
                    white-space: nowrap;
                }

                /* Dark mode support */
                @media (prefers-color-scheme: dark) {
                    .enoki-login-btn {
                        background: rgba(255, 255, 255, 0.08);
                        color: #1d1d1f;
                        border-color: rgba(255, 255, 255, 0.12);
                    }

                    .enoki-login-btn:hover {
                        background: rgba(255, 255, 255, 0.12);
                        border-color: rgba(255, 255, 255, 0.18);
                    }

                    .enoki-login-btn.logged-in {
                        background: rgba(255, 255, 255, 0.06);
                    }

                    .enoki-login-btn.logged-in:hover {
                        background: rgba(255, 255, 255, 0.1);
                    }
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .enoki-login-btn {
                        padding: 12px 20px;
                        font-size: 14px;
                    }
                }
            `}</style>
        </>
    );
};

export default EnokiLoginButton;
